/**
 * De API-client. Vervangt `@supabase/supabase-js` volledig.
 *
 * Wat er wegvalt is meer dan een bibliotheek. De Supabase-client deed drie dingen
 * die hier niet terugkomen:
 *
 *   1. `supabase.from('practices').select(...)` -- de browser stelde zelf query's
 *      op tegen de database. De RLS-policies bepaalden wat er terugkwam, maar de
 *      browser bepaalde wát er gevraagd werd, inclusief welke kolommen. Zo kwam
 *      `azure_api_key` in het beheerscherm terecht.
 *   2. `supabase.functions.invoke(...)` -- die wachtte op `getSession()` en kon
 *      daarbij een synchrone tokenvernieuwing uitlokken. SoepPage.tsx omzeilde de
 *      client daarom met een handmatige `fetch`, met een comment erbij dat het
 *      200 tot 800 ms van het kritieke pad scheelde.
 *   3. `supabase.auth` -- e-mail en wachtwoord. Dat is vervangen door Entra ID via
 *      App Service-authenticatie; er is hier geen aanmeldcode meer, alleen een
 *      omleiding.
 *
 * Alles loopt nu over dezelfde oorsprong als de pagina zelf. Geen CORS, geen
 * anon-key in de bundel, en het sessiecookie gaat automatisch mee.
 */

/**
 * Alle paden zijn relatief. De API en de SPA worden door dezelfde App Service
 * geserveerd, dus er is geen basis-URL nodig -- en dus ook geen omgevingsvariabele
 * die per omgeving anders staat en in de bundel belandt.
 */
const BASIS = '/api';

/**
 * Waar de browser naartoe moet om zich als beheerder aan te melden.
 *
 * Met de bestemming erin: zonder post_login_redirect_uri toont App Service na de
 * aanmelding zijn eigen tussenpagina, en de link daarop gaat naar de root -- waar
 * het opnamescherm staat, dat zonder HIS-token niets te bieden heeft. Zie
 * lib/entra.ts in de API; die route geeft dezelfde URL mee bij een 401.
 */
export const AANMELD_URL = '/.auth/login/aad?post_login_redirect_uri=%2Fadmin';
export const AFMELD_URL = '/.auth/logout';

// --- Fouten ----------------------------------------------------------------

/**
 * Een fout van de API, met de code erbij.
 *
 * De routes geven een `code` naast de melding: `sessie_ontbreekt`,
 * `geen_beheerder`, `pdf_taal_niet_ondersteund`. Die code is het aangrijpingspunt
 * voor de aanroeper -- op de Nederlandse tekst matchen zou breken zodra iemand een
 * melding herformuleert.
 */
export class ApiFout extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    /** Veldfouten uit een zod-validatie, als de route die meestuurt. */
    readonly velden?: { veld: string; melding: string }[],
  ) {
    super(message);
    this.name = 'ApiFout';
  }
}

/** True als deze fout betekent dat er (opnieuw) aangemeld moet worden. */
export function isAanmeldFout(fout: unknown): boolean {
  return fout instanceof ApiFout && fout.status === 401;
}

/** True als de sessie uit het HIS is verlopen of ingetrokken. */
export function isSessieVerlopen(fout: unknown): boolean {
  return (
    fout instanceof ApiFout &&
    (fout.code === 'sessie_ontbreekt' ||
      fout.code === 'praktijk_inactief' ||
      fout.code === 'gebruiker_inactief')
  );
}

/**
 * De melding met het advies erbij als opnieuw proberen zin heeft.
 *
 * Bij `ai_timeout` zegt de route wél wat er gebeurde -- Azure antwoordde niet binnen
 * de deadline -- maar niet wat de gebruiker nu moet. De aanroep is afgebroken
 * voordat er iets is vastgelegd, dus een tweede poging is veilig, en bij tijdelijke
 * drukte lukt die meestal wel. Dat advies staat hier en niet in de routetekst: de
 * server weet niet of de aanroeper iets te herhalen heeft.
 */
export function metHerhaalAdvies(melding: string, fout: unknown): string {
  if (!(fout instanceof ApiFout) || fout.code !== 'ai_timeout') return melding;
  return `${melding} Probeer het opnieuw.`;
}

interface FoutBody {
  error?: string;
  code?: string;
  velden?: { veld: string; melding: string }[];
}

// --- De kern ---------------------------------------------------------------

interface Opties {
  /** Query-parameters. Undefined en null worden weggelaten. */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Body; wordt als JSON verstuurd. */
  body?: unknown;
  signal?: AbortSignal;
}

function bouwUrl(pad: string, query?: Opties['query']): string {
  const url = `${BASIS}${pad}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [naam, waarde] of Object.entries(query)) {
    if (waarde === undefined || waarde === null || waarde === '') continue;
    params.set(naam, String(waarde));
  }

  const zoek = params.toString();
  return zoek ? `${url}?${zoek}` : url;
}

async function verzoek<T>(methode: string, pad: string, opties: Opties = {}): Promise<T> {
  const heeftBody = opties.body !== undefined;

  const antwoord = await fetch(bouwUrl(pad, opties.query), {
    method: methode,
    // Het sessiecookie is HttpOnly en same-origin; dit zorgt dat hij meegaat.
    credentials: 'same-origin',
    headers: heeftBody ? { 'Content-Type': 'application/json' } : {},
    ...(heeftBody ? { body: JSON.stringify(opties.body) } : {}),
    ...(opties.signal ? { signal: opties.signal } : {}),
  });

  if (!antwoord.ok) {
    // Een foutantwoord is JSON zolang het van de API komt. Een 502 van een proxy of
    // een 404 op een verkeerd pad is dat niet, en dan mag het parsen niet de
    // eigenlijke fout verbergen.
    let body: FoutBody = {};
    try {
      body = (await antwoord.json()) as FoutBody;
    } catch {
      /* geen JSON; de status zegt genoeg */
    }

    throw new ApiFout(
      body.error ?? `Onverwacht antwoord van de server (${antwoord.status}).`,
      antwoord.status,
      body.code ?? 'onbekend',
      body.velden,
    );
  }

  // 204 en een lege body komen voor bij DELETE.
  if (antwoord.status === 204) return undefined as T;

  const type = antwoord.headers.get('content-type') ?? '';
  if (!type.includes('application/json')) return undefined as T;

  return (await antwoord.json()) as T;
}

export const api = {
  get: <T>(pad: string, opties?: Omit<Opties, 'body'>) => verzoek<T>('GET', pad, opties),
  post: <T>(pad: string, body?: unknown, opties?: Omit<Opties, 'body'>) =>
    verzoek<T>('POST', pad, { ...opties, body: body ?? {} }),
  patch: <T>(pad: string, body: unknown, opties?: Omit<Opties, 'body'>) =>
    verzoek<T>('PATCH', pad, { ...opties, body }),
  delete: <T>(pad: string, opties?: Omit<Opties, 'body'>) => verzoek<T>('DELETE', pad, opties),

  /**
   * Een binair antwoord ophalen, zoals de patiënt-PDF en de TTS-audio.
   *
   * Apart van `get` omdat het resultaat geen JSON is. De foutbehandeling is
   * hetzelfde: een mislukte aanroep geeft nog steeds een JSON-body met een code, en
   * die moet als ApiFout naar boven komen en niet als een onleesbare blob.
   */
  async blob(pad: string, opties: Opties = {}): Promise<Blob> {
    const { blob } = await this.blobMetHeaders(pad, opties);
    return blob;
  },

  /**
   * Als blob, maar met de responseheaders erbij.
   *
   * Nodig voor /api/speech/tts: de body is audio, dus wat die aanroep kost staat in
   * een header. Zonder dit zou de client de prijs per seconde moeten kennen om de
   * kostenteller te kunnen bijwerken -- en dat is precies wat fase 6 weghaalt.
   */
  async blobMetHeaders(pad: string, opties: Opties = {}): Promise<{ blob: Blob; headers: Headers }> {
    const heeftBody = opties.body !== undefined;

    const antwoord = await fetch(bouwUrl(pad, opties.query), {
      method: heeftBody ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: heeftBody ? { 'Content-Type': 'application/json' } : {},
      ...(heeftBody ? { body: JSON.stringify(opties.body) } : {}),
      ...(opties.signal ? { signal: opties.signal } : {}),
    });

    if (!antwoord.ok) {
      let body: FoutBody = {};
      try {
        body = (await antwoord.json()) as FoutBody;
      } catch {
        /* geen JSON */
      }
      throw new ApiFout(
        body.error ?? `Onverwacht antwoord van de server (${antwoord.status}).`,
        antwoord.status,
        body.code ?? 'onbekend',
      );
    }

    return { blob: await antwoord.blob(), headers: antwoord.headers };
  },
};
